import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Package,
  Truck,
  ShieldCheck,
  CheckCircle2,
  MapPin,
  Calendar,
  Loader2,
  AlertCircle,
  Anchor,
  Factory,
  Bell,
  ClipboardList,
  Copy,
  Mail,
  MessageCircle,
  Phone,
  Route,
  Shield,
  Smartphone,
  TimerReset,
  Share2,
  X,
  Clock3,
  ArrowRight,
  Download,
  Wifi,
  WifiOff,
  Clipboard,
  RefreshCcw,
} from "lucide-react";
import { STATUS_PROGRESS, STATUS_STEPS, STATUS_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import useAppInstall from "@/hooks/useAppInstall";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { pickLanguage } from "@/lib/publicLanguage";

const RECENT_TRACKING_KEY = "rsi_recent_tracking_ids";

const STATUS_ICONS = {
  sourced: Factory,
  processing: Package,
  in_transit: Truck,
  customs: ShieldCheck,
  out_for_delivery: Anchor,
  delivered: CheckCircle2,
  delayed: AlertCircle,
};

const STATUS_LABELS_BY_LANGUAGE = {
  en: STATUS_LABEL,
  "zh-CN": {
    sourced: "已采购",
    processing: "处理中",
    in_transit: "运输中",
    customs: "清关中",
    out_for_delivery: "派送中",
    delivered: "已送达",
    delayed: "延误",
  },
  bn: {
    sourced: "সংগ্রহিত",
    processing: "প্রক্রিয়াধীন",
    in_transit: "পথে আছে",
    customs: "কাস্টমস ক্লিয়ারেন্স",
    out_for_delivery: "ডেলিভারির পথে",
    delivered: "ডেলিভার হয়েছে",
    delayed: "বিলম্বিত",
  },
};

function getStatusLabels(language) {
  return STATUS_LABELS_BY_LANGUAGE[language] || STATUS_LABELS_BY_LANGUAGE.en;
}

function formatDate(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDateShort(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function etaWindow(shipment, locale) {
  if (!shipment) return "—";
  if (shipment.eta_start && shipment.eta_end) {
    return `${formatDateShort(shipment.eta_start, locale)} - ${formatDateShort(shipment.eta_end, locale)}`;
  }
  return shipment.eta || "—";
}

function progressValue(shipment) {
  if (!shipment) return 0;
  if (Number.isFinite(Number(shipment.progress_percent))) {
    return Math.max(0, Math.min(100, Number(shipment.progress_percent)));
  }
  return STATUS_PROGRESS[shipment.current_status] ?? 0;
}

function readRecentTracking() {
  try {
    const raw = localStorage.getItem(RECENT_TRACKING_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentTracking(shipment) {
  if (!shipment?.tracking_id) return [];
  const next = [
    {
      tracking_id: shipment.tracking_id,
      current_status: shipment.current_status,
      current_location: shipment.current_location,
      updated_at: shipment.updated_at,
      customer_name: shipment.customer_name,
    },
    ...readRecentTracking().filter((item) => item.tracking_id !== shipment.tracking_id),
  ].slice(0, 4);
  localStorage.setItem(RECENT_TRACKING_KEY, JSON.stringify(next));
  return next;
}

function removeRecentTracking(trackingId) {
  const next = readRecentTracking().filter((item) => item.tracking_id !== trackingId);
  localStorage.setItem(RECENT_TRACKING_KEY, JSON.stringify(next));
  return next;
}

function nextMilestone(status, statusLabels, completedLabel) {
  const idx = STATUS_STEPS.findIndex((step) => step.key === status);
  if (idx < 0 || idx >= STATUS_STEPS.length - 1) return completedLabel;
  return statusLabels[STATUS_STEPS[idx + 1].key] || STATUS_STEPS[idx + 1].label;
}

function statusGuidance(shipment, language, statusLabels, completedLabel) {
  if (!shipment) return "";
  const status = shipment.current_status;
  if (status === "delayed") {
    return shipment.delay_reason
      ? pickLanguage(language, {
          en: `Shipment is delayed. Please monitor the next checkpoint update while our team resolves: ${shipment.delay_reason}`,
          "zh-CN": `货运已延误。请关注下一次节点更新，我们的团队正在处理：${shipment.delay_reason}`,
          bn: `চালানটি বিলম্বিত হয়েছে। আমাদের দল কারণটি সমাধান করার সময় অনুগ্রহ করে পরবর্তী চেকপয়েন্ট আপডেট লক্ষ্য করুন: ${shipment.delay_reason}`,
        })
      : pickLanguage(language, {
          en: "Shipment is delayed. Our operations team is reviewing the cause and will publish the next update as soon as movement resumes.",
          "zh-CN": "货运已延误。我们的运营团队正在排查原因，并会在恢复流转后尽快发布下一次更新。",
          bn: "চালানটি বিলম্বিত হয়েছে। আমাদের অপারেশন দল কারণ পর্যালোচনা করছে এবং চলাচল পুনরায় শুরু হলে দ্রুত পরবর্তী আপডেট দেবে।",
        });
  }
  if (status === "delivered") {
    return pickLanguage(language, {
      en: "Shipment has been marked as delivered. Please review the delivery details and proof of delivery below.",
      "zh-CN": "货运已标记为送达。请在下方查看送达详情与签收凭证。",
      bn: "চালানটি ডেলিভার হয়েছে হিসেবে চিহ্নিত করা হয়েছে। নিচে ডেলিভারির তথ্য ও প্রমাণ দেখে নিন।",
    });
  }
  if (status === "customs") {
    return pickLanguage(language, {
      en: "Shipment is currently in customs-related processing. Clearance timing depends on destination procedures, documentation review, and port handling.",
      "zh-CN": "货运当前正处于清关相关流程中。放行时间取决于目的地流程、文件审核以及港口处理进度。",
      bn: "চালানটি বর্তমানে কাস্টমস-সংক্রান্ত প্রক্রিয়ায় রয়েছে। ক্লিয়ারেন্সের সময় গন্তব্য দেশের প্রক্রিয়া, নথি যাচাই ও বন্দর পরিচালনার ওপর নির্ভর করে।",
    });
  }
  if (status === "out_for_delivery") {
    return pickLanguage(language, {
      en: "Shipment has cleared the main transit stages and is currently moving toward the consignee delivery point.",
      "zh-CN": "货运已完成主要运输阶段，当前正送往收货方交付地点。",
      bn: "চালানটি মূল ট্রানজিট ধাপগুলো সম্পন্ন করেছে এবং এখন গ্রহণকারীর ডেলিভারি পয়েন্টের দিকে এগোচ্ছে।",
    });
  }
  return pickLanguage(language, {
    en: `Current shipment activity is ${statusLabels[status] || status}. The next operational milestone is ${nextMilestone(status, statusLabels, completedLabel)}.`,
    "zh-CN": `当前货运状态为${statusLabels[status] || status}。下一运营里程碑是${nextMilestone(status, statusLabels, completedLabel)}。`,
    bn: `বর্তমান চালানের অবস্থা ${statusLabels[status] || status}। পরবর্তী অপারেশনাল মাইলস্টোন হলো ${nextMilestone(status, statusLabels, completedLabel)}।`,
  });
}

export default function Track() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trackingId, setTrackingId] = useState("");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savedShipments, setSavedShipments] = useState([]);
  const { canInstall, install, isStandalone, isOnline, isIOS } = useAppInstall();
  const { language } = useSiteLanguage();
  const locale = language === "bn" ? "bn-BD" : language === "zh-CN" ? "zh-CN" : undefined;
  const statusLabels = getStatusLabels(language);
  const completedLabel = pickLanguage(language, {
    en: "Completed",
    "zh-CN": "已完成",
    bn: "সম্পন্ন",
  });
  const statusSteps = STATUS_STEPS.map((step) => ({
    ...step,
    label: statusLabels[step.key] || step.label,
  }));
  const copy = {
    clipboardUnavailable: pickLanguage(language, {
      en: "Clipboard access is not available",
      "zh-CN": "无法访问剪贴板",
      bn: "ক্লিপবোর্ড ব্যবহার করা যাচ্ছে না",
    }),
    trackingIdPasted: pickLanguage(language, {
      en: "Tracking ID pasted",
      "zh-CN": "追踪编号已粘贴",
      bn: "ট্র্যাকিং আইডি পেস্ট করা হয়েছে",
    }),
    pasteUnavailable: pickLanguage(language, {
      en: "Paste is not available on this device",
      "zh-CN": "该设备不支持粘贴",
      bn: "এই ডিভাইসে পেস্ট করা যাচ্ছে না",
    }),
    trackingLinkCopied: pickLanguage(language, {
      en: "Tracking link copied",
      "zh-CN": "追踪链接已复制",
      bn: "ট্র্যাকিং লিংক কপি করা হয়েছে",
    }),
    appLinkCopied: pickLanguage(language, {
      en: "App link copied",
      "zh-CN": "应用链接已复制",
      bn: "অ্যাপ লিংক কপি করা হয়েছে",
    }),
    appShareTitle: pickLanguage(language, {
      en: "R.S. International Shipment Tracking App",
      "zh-CN": "R.S. International 货运追踪应用",
      bn: "R.S. International চালান ট্র্যাকিং অ্যাপ",
    }),
    appShareText: pickLanguage(language, {
      en: "Open the shipment tracking app",
      "zh-CN": "打开货运追踪应用",
      bn: "চালান ট্র্যাকিং অ্যাপ খুলুন",
    }),
    shipmentShareText: pickLanguage(language, {
      en: "Track shipment",
      "zh-CN": "追踪货运",
      bn: "চালান ট্র্যাক করুন",
    }),
    heroBadge: pickLanguage(language, {
      en: "Live Shipment Tracking",
      "zh-CN": "实时货运追踪",
      bn: "লাইভ চালান ট্র্যাকিং",
    }),
    heroTitle: pickLanguage(language, {
      en: "Where's my shipment?",
      "zh-CN": "我的货到哪里了？",
      bn: "আমার চালান কোথায়?",
    }),
    heroBody: pickLanguage(language, {
      en: "Enter your R.S. International tracking ID below to view live progress, ETA window, route checkpoints, proof of delivery, and shipment alerts.",
      "zh-CN": "在下方输入您的 R.S. International 追踪编号，即可查看实时进度、预计到达时间窗口、路线节点、送达凭证和货运提醒。",
      bn: "নিচে আপনার R.S. International ট্র্যাকিং আইডি লিখে লাইভ অগ্রগতি, ETA উইন্ডো, রুট চেকপয়েন্ট, ডেলিভারি প্রমাণ এবং চালান অ্যালার্ট দেখুন।",
    }),
    liveConnection: pickLanguage(language, {
      en: "Live connection",
      "zh-CN": "实时连接",
      bn: "লাইভ সংযোগ",
    }),
    offlineMode: pickLanguage(language, {
      en: "Offline mode",
      "zh-CN": "离线模式",
      bn: "অফলাইন মোড",
    }),
    openedAsApp: pickLanguage(language, {
      en: "Opened as app",
      "zh-CN": "以应用方式打开",
      bn: "অ্যাপ হিসেবে খোলা",
    }),
    webAppReady: pickLanguage(language, {
      en: "Web app ready",
      "zh-CN": "网页应用已就绪",
      bn: "ওয়েব অ্যাপ প্রস্তুত",
    }),
    resume: pickLanguage(language, {
      en: "Resume",
      "zh-CN": "继续查看",
      bn: "আবার খুলুন",
    }),
    inputPlaceholder: pickLanguage(language, {
      en: "Enter tracking ID e.g. RSI-2026-0001",
      "zh-CN": "输入追踪编号，例如 RSI-2026-0001",
      bn: "ট্র্যাকিং আইডি লিখুন, যেমন RSI-2026-0001",
    }),
    paste: pickLanguage(language, {
      en: "Paste",
      "zh-CN": "粘贴",
      bn: "পেস্ট",
    }),
    clear: pickLanguage(language, {
      en: "Clear",
      "zh-CN": "清除",
      bn: "মুছুন",
    }),
    tracking: pickLanguage(language, {
      en: "Tracking…",
      "zh-CN": "追踪中……",
      bn: "ট্র্যাক হচ্ছে…",
    }),
    trackShipment: pickLanguage(language, {
      en: "Track shipment",
      "zh-CN": "追踪货运",
      bn: "চালান ট্র্যাক করুন",
    }),
    tryDemoId: pickLanguage(language, {
      en: "Try a demo ID:",
      "zh-CN": "试试演示编号：",
      bn: "একটি ডেমো আইডি চেষ্টা করুন:",
    }),
  };

  useEffect(() => {
    setSavedShipments(readRecentTracking());
  }, []);

  const lookupShipment = async (rawId, { syncInput = true, syncUrl = true } = {}) => {
    const nextId = rawId.trim().toUpperCase();
    if (!nextId) return;
    setLoading(true);
    setError("");
    if (syncInput) setTrackingId(nextId);
    try {
      const { data } = await api.get(`/track/${nextId}`);
      setShipment(data);
      setSavedShipments(saveRecentTracking(data));
      if (syncUrl) setSearchParams({ id: nextId });
    } catch (err) {
      setShipment(null);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      lookupShipment(idFromUrl, { syncInput: true, syncUrl: false });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    await lookupShipment(trackingId);
  };

  const loadSaved = async (nextId) => {
    await lookupShipment(nextId);
  };

  const currentStepIdx = shipment
    ? STATUS_STEPS.findIndex((s) => s.key === shipment.current_status)
    : -1;
  const progress = progressValue(shipment);
  const lastEvent = shipment?.events?.length ? shipment.events[shipment.events.length - 1] : null;
  const shareUrl =
    shipment && typeof window !== "undefined"
      ? `${window.location.origin}/track?id=${encodeURIComponent(shipment.tracking_id)}`
      : "";
  const notificationChannels = useMemo(
    () =>
      shipment
        ? [
            shipment.notifications_email_enabled && {
              label: pickLanguage(language, { en: "Email alerts", "zh-CN": "邮件提醒", bn: "ইমেইল অ্যালার্ট" }),
              icon: Mail,
            },
            shipment.notifications_sms_enabled && {
              label: pickLanguage(language, { en: "SMS alerts", "zh-CN": "短信提醒", bn: "এসএমএস অ্যালার্ট" }),
              icon: Smartphone,
            },
            shipment.notifications_whatsapp_enabled && {
              label: pickLanguage(language, { en: "WhatsApp alerts", "zh-CN": "WhatsApp 提醒", bn: "হোয়াটসঅ্যাপ অ্যালার্ট" }),
              icon: MessageCircle,
            },
          ].filter(Boolean)
        : [],
    [language, shipment]
  );
  const totalEvents = shipment?.events?.length || 0;
  const guidanceText = statusGuidance(shipment, language, statusLabels, completedLabel);
  const lastTracked = savedShipments[0] || null;

  const copyText = async (value, successText) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successText);
    } catch {
      toast.error(copy.clipboardUnavailable);
    }
  };

  const pasteTrackingId = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setTrackingId(text.toUpperCase().trim());
      toast.success(copy.trackingIdPasted);
    } catch {
      toast.error(copy.pasteUnavailable);
    }
  };

  const shareTracking = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Shipment ${shipment.tracking_id}`,
          text: `${copy.shipmentShareText} ${shipment.tracking_id}`,
          url: shareUrl,
        });
      } else {
        await copyText(shareUrl, copy.trackingLinkCopied);
      }
    } catch {
      // Ignore canceled share actions.
    }
  };

  const shareApp = async () => {
    const url = typeof window !== "undefined" ? `${window.location.origin}/track` : "/track";
    try {
      if (navigator.share) {
        await navigator.share({
          title: copy.appShareTitle,
          text: copy.appShareText,
          url,
        });
      } else {
        await copyText(url, copy.appLinkCopied);
      }
    } catch {
      // Ignore canceled share actions.
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="py-20 relative overflow-hidden" data-testid="track-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--rsi-purple)] via-[var(--rsi-navy)] to-[var(--rsi-purple)] grain" />
        <div className="container-rsi relative text-white">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
            <Truck className="h-3.5 w-3.5 text-[#9be0b3]" /> {copy.heroBadge}
          </span>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-4 leading-[1.02]">
            {copy.heroTitle}
          </h1>
          <p className="mt-4 text-white/85 max-w-xl">
            {copy.heroBody}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-white/90">
              {isOnline ? <Wifi className="h-3.5 w-3.5 text-[#9be0b3]" /> : <WifiOff className="h-3.5 w-3.5 text-amber-300" />}
              {isOnline ? copy.liveConnection : copy.offlineMode}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-white/90">
              <Smartphone className="h-3.5 w-3.5 text-[#bfa3ff]" />
              {isStandalone ? copy.openedAsApp : copy.webAppReady}
            </span>
            {lastTracked ? (
              <button
                type="button"
                onClick={() => loadSaved(lastTracked.tracking_id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-white/90 hover:bg-white/15"
              >
                <RefreshCcw className="h-3.5 w-3.5 text-[#bfa3ff]" />
                {copy.resume} {lastTracked.tracking_id}
              </button>
            ) : null}
          </div>

          <div className="mt-10 grid xl:grid-cols-[1.05fr,0.95fr] gap-6 items-start">
            <div>
              <form onSubmit={submit} className="max-w-2xl">
                <div className="glass rounded-[30px] p-3 flex flex-wrap items-center gap-2 shadow-[0_28px_70px_-28px_rgba(12,7,22,0.55)]">
                  <div className="ml-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--rsi-purple)]/8 text-[var(--rsi-purple)]">
                    <Search className="h-5 w-5" />
                  </div>
                  <input
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                    placeholder={copy.inputPlaceholder}
                    data-testid="tracking-input"
                    className="min-w-[220px] flex-1 bg-transparent outline-none px-2 py-3 text-gray-900 font-mono-plex placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={pasteTrackingId}
                    className="btn-ghost !py-2.5 !px-4 text-xs"
                  >
                    <Clipboard className="h-4 w-4" /> {copy.paste}
                  </button>
                  {trackingId ? (
                    <button
                      type="button"
                      onClick={() => setTrackingId("")}
                      className="btn-ghost !py-2.5 !px-4 text-xs"
                    >
                      {copy.clear}
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary !py-3 !px-5"
                    data-testid="tracking-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> {copy.tracking}
                      </>
                    ) : (
                      <>
                        {copy.trackShipment} <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
                <div className="mt-3 ml-3 flex flex-wrap items-center gap-2 text-xs text-white/65">
                  <span>{copy.tryDemoId}</span>
                  <code className="rounded-full border border-white/12 bg-white/10 px-3 py-1 font-mono-plex text-white/85">RSI-2026-0001</code>
                  <code className="rounded-full border border-white/12 bg-white/10 px-3 py-1 font-mono-plex text-white/85">RSI-2026-0002</code>
                  <code className="rounded-full border border-white/12 bg-white/10 px-3 py-1 font-mono-plex text-white/85">RSI-2026-0003</code>
                </div>
              </form>

              <div className="mt-8 grid xl:grid-cols-[1.1fr,0.9fr] gap-5 items-stretch">
                <PWAInstallPrompt
                  embedded
                  title={pickLanguage(language, {
                    en: "Install or open the customer app",
                    "zh-CN": "安装或打开客户应用",
                    bn: "কাস্টমার অ্যাপ ইনস্টল বা খুলুন",
                  })}
                  description={pickLanguage(language, {
                    en: "A more polished mobile workspace for product discovery, order follow-up, and shipment tracking, launched only from this section.",
                    "zh-CN": "更完善的移动工作区，用于产品浏览、订单跟进和货运追踪，并从此区域启动。",
                    bn: "পণ্য খোঁজা, অর্ডার ফলো-আপ এবং চালান ট্র্যাকিংয়ের জন্য আরও পরিপাটি একটি মোবাইল ওয়ার্কস্পেস, যা এই অংশ থেকেই চালু হয়।",
                  })}
                  chips={[
                    pickLanguage(language, { en: "Customer workspace", "zh-CN": "客户工作区", bn: "গ্রাহক ওয়ার্কস্পেস" }),
                    pickLanguage(language, { en: "Android + iPhone ready", "zh-CN": "支持 Android + iPhone", bn: "Android + iPhone প্রস্তুত" }),
                    pickLanguage(language, { en: "Admin connected", "zh-CN": "已连接后台", bn: "অ্যাডমিন সংযুক্ত" }),
                  ]}
                />
                <div className="surface-card px-5 py-5 text-left">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-[var(--rsi-navy)]">
                        {pickLanguage(language, {
                          en: "App distribution and customer access",
                          "zh-CN": "应用分发与客户访问",
                          bn: "অ্যাপ বিতরণ ও গ্রাহক প্রবেশাধিকার",
                        })}
                      </div>
                      <div className="mt-1 max-w-md text-sm text-gray-600 leading-relaxed">
                        {pickLanguage(language, {
                          en: "This section is now the main launch zone for the app, with cleaner install entry, customer-specific access, and direct connection to the website and admin system.",
                          "zh-CN": "该区域现在是应用的主要启动入口，提供更清晰的安装方式、客户专属访问以及与网站和后台系统的直接连接。",
                          bn: "এই অংশটি এখন অ্যাপের প্রধান লঞ্চ জোন, যেখানে আরও পরিষ্কার ইনস্টল এন্ট্রি, গ্রাহকভিত্তিক অ্যাক্সেস এবং ওয়েবসাইট ও অ্যাডমিন সিস্টেমের সরাসরি সংযোগ রয়েছে।",
                        })}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      <span className="badge-purple">{pickLanguage(language, { en: "Private customer access", "zh-CN": "私有客户访问", bn: "ব্যক্তিগত গ্রাহক প্রবেশাধিকার" })}</span>
                      <span className="badge-purple">{pickLanguage(language, { en: "Order-specific visibility", "zh-CN": "订单级可见性", bn: "অর্ডারভিত্তিক দৃশ্যমানতা" })}</span>
                      <span className="badge-purple">{pickLanguage(language, { en: "Store-wrapper ready", "zh-CN": "支持商店封装", bn: "স্টোর-র্যাপার প্রস্তুত" })}</span>
                    </div>
                  </div>
                  <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
                    <Link to="/app" className="rounded-[24px] border border-[var(--rsi-purple)]/10 bg-[var(--rsi-purple-tint)] px-4 py-4 text-[var(--rsi-navy)] transition hover:-translate-y-0.5 hover:border-[var(--rsi-purple)]/30">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold">Open customer app</div>
                        <Smartphone className="h-4 w-4 text-[var(--rsi-purple)]" />
                      </div>
                      <div className="mt-2 text-xs text-gray-600">Use the full app workspace on the website now.</div>
                    </Link>
                    <div className="rounded-[24px] border border-gray-100 bg-white px-4 py-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-[var(--rsi-navy)]">Google Play path</div>
                        <Shield className="h-4 w-4 text-[var(--rsi-green)]" />
                      </div>
                      <div className="mt-2 text-xs text-gray-500">Prepared for Android packaging and store submission workflow.</div>
                    </div>
                    <div className="rounded-[24px] border border-gray-100 bg-white px-4 py-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-[var(--rsi-navy)]">App Store path</div>
                        <TimerReset className="h-4 w-4 text-[var(--rsi-purple)]" />
                      </div>
                      <div className="mt-2 text-xs text-gray-500">Prepared for iPhone-safe app shell and native wrapper delivery.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="premium-panel app-preview-grid px-6 py-6 text-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="app-showcase-chip">
                  <Smartphone className="h-3.5 w-3.5" /> Customer app preview
                </span>
                <span className="app-showcase-chip">
                  <Clock3 className="h-3.5 w-3.5" /> Faster return access
                </span>
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight">
                A cleaner, more premium mobile entry for tracking and customer access.
              </h2>
              <p className="mt-3 max-w-lg text-sm sm:text-base text-white/74 leading-relaxed">
                This app section now feels more like a real product surface: install prompt, workspace access,
                customer-specific visibility, and stronger visual hierarchy for first-time and returning users.
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="metric-tile">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">Experience</div>
                  <div className="mt-2 text-lg font-bold text-white">App-like launch flow</div>
                  <div className="mt-2 text-sm text-white/68">Install, reopen, and return to active tracking with less friction.</div>
                </div>
                <div className="metric-tile">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">Security</div>
                  <div className="mt-2 text-lg font-bold text-white">Customer-specific access</div>
                  <div className="mt-2 text-sm text-white/68">Each customer can use their own access identity and view only their own data.</div>
                </div>
              </div>

              <div className="mt-6 rounded-[30px] border border-white/12 bg-white/8 p-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-white/12 grid place-items-center">
                    <ShieldCheck className="h-5 w-5 text-[#9be0b3]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Designed for customer confidence</div>
                    <div className="mt-1 text-xs text-white/65">
                      Better spacing, stronger CTA grouping, and clearer app/store messaging across this section.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="metric-tile">
              <div className="text-xs uppercase tracking-[0.18em] text-white/50">App mode</div>
              <div className="mt-2 text-lg font-bold text-white">{isStandalone ? "Installed experience" : "Browser experience"}</div>
              <div className="mt-2 text-sm text-white/70">
                {isStandalone
                  ? "The tracking app is opened in standalone mode for a cleaner mobile workflow."
                  : "Install the app for faster launch, stronger mobile UX, and easier return access."}
              </div>
            </div>
            <div className="metric-tile">
              <div className="text-xs uppercase tracking-[0.18em] text-white/50">Connection</div>
              <div className="mt-2 text-lg font-bold text-white">{isOnline ? "Live tracking ready" : "Offline fallback active"}</div>
              <div className="mt-2 text-sm text-white/70">
                {isOnline
                  ? "Realtime shipment lookups and latest checkpoints are available."
                  : "Saved tracking IDs remain available until the connection returns."}
              </div>
            </div>
            <div className="metric-tile">
              <div className="text-xs uppercase tracking-[0.18em] text-white/50">Quick action</div>
              <div className="mt-2 text-lg font-bold text-white">Open last shipment</div>
              <div className="mt-2 text-sm text-white/70">
                {lastTracked ? `${lastTracked.tracking_id} is ready to reopen from this device.` : "Track one shipment to save it locally in this app."}
              </div>
            </div>
            <div className="metric-tile">
              <div className="text-xs uppercase tracking-[0.18em] text-white/50">Install</div>
              <div className="mt-2 text-lg font-bold text-white">{canInstall ? "One-tap install" : isIOS ? "iPhone install ready" : "Share app access"}</div>
              <div className="mt-2 text-sm text-white/70">
                {canInstall
                  ? "This device can install the tracking app directly from the browser."
                  : isIOS
                    ? "Use iPhone Share menu and choose Add to Home Screen."
                    : "Share the tracking app with customers or colleagues instantly."}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={shareApp} className="btn-outline-light">
              <Share2 className="h-4 w-4" /> Share app
            </button>
            <Link to="/app" className="btn-outline-light">
              <Smartphone className="h-4 w-4" /> Open app workspace
            </Link>
          </div>

          {savedShipments.length > 0 && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Saved in this app</div>
              <div className="mt-3 flex flex-wrap gap-3">
                {savedShipments.map((item) => (
                  <div
                    key={item.tracking_id}
                    className="glass rounded-2xl px-4 py-3 text-left min-w-[220px] hover:bg-white/15 transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button type="button" onClick={() => loadSaved(item.tracking_id)} className="text-left flex-1">
                        <div className="font-mono-plex text-sm text-white">{item.tracking_id}</div>
                        <div className="text-xs text-white/70 mt-1">{STATUS_LABEL[item.current_status] || item.current_status}</div>
                        <div className="text-xs text-white/55 mt-1">{item.current_location || "Tap to reopen tracking"}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSavedShipments(removeRecentTracking(item.tracking_id))}
                        className="h-8 w-8 rounded-full grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                        aria-label={`Remove ${item.tracking_id}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-16 bg-[var(--rsi-bg-subtle)] min-h-[40vh]">
        <div className="container-rsi">
          {!isOnline && (
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
              You are currently offline. Saved tracking IDs still work inside the app, but live shipment refresh requires an internet connection.
            </div>
          )}
          {error && (
            <div
              className="rsi-card p-6 flex items-start gap-3 border-red-200"
              data-testid="tracking-error"
            >
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <div className="font-semibold text-red-700">
                  {pickLanguage(language, { en: "No shipment found", "zh-CN": "未找到货运信息", bn: "কোনো চালান পাওয়া যায়নি" })}
                </div>
                <div className="text-sm text-gray-600 mt-1">{error}</div>
              </div>
            </div>
          )}

          {shipment && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="tracking-result"
            >
              {/* Header */}
              <div className="rsi-card p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Tracking ID</div>
                    <div className="font-mono-plex font-bold text-2xl text-[var(--rsi-navy)]" data-testid="result-tracking-id">
                      {shipment.tracking_id}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="badge-purple">
                      <Truck className="h-3 w-3" /> {statusLabels[shipment.current_status] || shipment.current_status}
                    </span>
                    <div className="text-xs text-gray-500 mt-2">
                      {pickLanguage(language, { en: "Last updated", "zh-CN": "最后更新", bn: "সর্বশেষ আপডেট" })} {formatDate(shipment.updated_at, locale)}
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">{pickLanguage(language, { en: "Customer", "zh-CN": "客户", bn: "গ্রাহক" })}</div>
                    <div className="font-medium text-[var(--rsi-navy)] mt-1">{shipment.customer_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">{pickLanguage(language, { en: "Origin", "zh-CN": "始发地", bn: "উৎপত্তি" })}</div>
                    <div className="font-medium text-[var(--rsi-navy)] mt-1 flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> {shipment.origin || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">{pickLanguage(language, { en: "Destination", "zh-CN": "目的地", bn: "গন্তব্য" })}</div>
                    <div className="font-medium text-[var(--rsi-navy)] mt-1 flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> {shipment.destination || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">{pickLanguage(language, { en: "ETA Window", "zh-CN": "预计到达窗口", bn: "ETA উইন্ডো" })}</div>
                    <div className="font-mono-plex font-medium text-[var(--rsi-navy)] mt-1 flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> {etaWindow(shipment, locale)}
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-[var(--rsi-purple)]/10 bg-[var(--rsi-purple-tint)]/60 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[var(--rsi-purple)]/70">
                        {pickLanguage(language, { en: "Shipment progress", "zh-CN": "货运进度", bn: "চালানের অগ্রগতি" })}
                      </div>
                      <div className="mt-2 text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{progress}%</div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {notificationChannels.length > 0 ? (
                        notificationChannels.map((item) => {
                          const Icon = item.icon;
                          return (
                            <span key={item.label} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[var(--rsi-purple)] border border-[var(--rsi-purple)]/10">
                              <Icon className="h-3.5 w-3.5" /> {item.label}
                            </span>
                          );
                        })
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-gray-500 border border-gray-200">
                          <Bell className="h-3.5 w-3.5" /> {pickLanguage(language, {
                            en: "Notifications not enabled",
                            "zh-CN": "未启用提醒",
                            bn: "নোটিফিকেশন চালু নেই",
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 h-3 rounded-full bg-white">
                    <div
                      className="h-3 rounded-full bg-gradient-to-r from-[var(--rsi-purple)] to-[var(--rsi-navy)] transition-all duration-700"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Route className="h-4 w-4 text-[var(--rsi-purple)]" /> {shipment.current_location || pickLanguage(language, {
                        en: "Checkpoint pending",
                        "zh-CN": "等待下一节点",
                        bn: "পরবর্তী চেকপয়েন্টের অপেক্ষায়",
                      })}
                    </span>
                    {lastEvent?.note && <span>{lastEvent.note}</span>}
                  </div>
                </div>

                {shipment.delay_reason && (
                  <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <span className="font-semibold">
                      {pickLanguage(language, { en: "Delay notice:", "zh-CN": "延误通知：", bn: "বিলম্ব বিজ্ঞপ্তি:" })}
                    </span>{" "}
                    {shipment.delay_reason}
                  </div>
                )}

                <div className="mt-5 grid md:grid-cols-4 gap-4 text-sm">
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Current checkpoint", "zh-CN": "当前节点", bn: "বর্তমান চেকপয়েন্ট" })}</div>
                    <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{shipment.current_location || pickLanguage(language, { en: "Pending update", "zh-CN": "等待更新", bn: "আপডেটের অপেক্ষায়" })}</div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Next milestone", "zh-CN": "下一里程碑", bn: "পরবর্তী মাইলস্টোন" })}</div>
                    <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{nextMilestone(shipment.current_status, statusLabels, completedLabel)}</div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Event count", "zh-CN": "事件数量", bn: "ইভেন্ট সংখ্যা" })}</div>
                    <div className="mt-2 font-semibold text-[var(--rsi-navy)]">
                      {totalEvents} {pickLanguage(language, { en: "checkpoints", "zh-CN": "个节点", bn: "টি চেকপয়েন্ট" })}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white p-4">
                    <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Last scan", "zh-CN": "最后扫描", bn: "সর্বশেষ স্ক্যান" })}</div>
                    <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{lastEvent ? formatDate(lastEvent.timestamp, locale) : pickLanguage(language, { en: "Pending", "zh-CN": "待处理", bn: "অপেক্ষমাণ" })}</div>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-[var(--rsi-purple)]/10 bg-white p-5">
                  <div className="flex items-start gap-3">
                    <Clock3 className="h-5 w-5 mt-0.5 text-[var(--rsi-purple)]" />
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[var(--rsi-purple)]/70">{pickLanguage(language, { en: "Operations guidance", "zh-CN": "操作指引", bn: "অপারেশন নির্দেশনা" })}</div>
                      <div className="mt-2 text-sm text-gray-700 leading-relaxed">{guidanceText}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid lg:grid-cols-[1.5fr,1fr] gap-8">
                <div className="rsi-card p-6 lg:p-8">
                  <h3 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)] mb-6">
                    {pickLanguage(language, { en: "Shipment details", "zh-CN": "货运详情", bn: "চালানের বিস্তারিত" })}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Cargo summary", "zh-CN": "货物概要", bn: "কার্গো সারাংশ" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">{shipment.product_summary || "—"}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Carrier / Mode", "zh-CN": "承运方 / 运输方式", bn: "ক্যারিয়ার / মোড" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">{shipment.carrier || shipment.vessel_or_flight || "—"}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Order / Reference", "zh-CN": "订单 / 参考号", bn: "অর্ডার / রেফারেন্স" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">{shipment.reference_no || "—"}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Container", "zh-CN": "集装箱", bn: "কনটেইনার" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">{shipment.container_no || "—"}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Packages / Weight", "zh-CN": "件数 / 重量", bn: "প্যাকেজ / ওজন" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">
                        {shipment.package_count || "—"} {shipment.total_weight ? `· ${shipment.total_weight}` : ""}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Trade term", "zh-CN": "贸易条款", bn: "ট্রেড টার্ম" })}</div>
                      <div className="mt-2 text-[var(--rsi-navy)] font-medium">{shipment.incoterm || "—"}</div>
                    </div>
                  </div>

                  <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
                    <div className="rounded-xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Current checkpoint", "zh-CN": "当前节点", bn: "বর্তমান চেকপয়েন্ট" })}</div>
                      <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{shipment.current_location || "—"}</div>
                    </div>
                    <div className="rounded-xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Delivery contact", "zh-CN": "收货联系人", bn: "ডেলিভারি যোগাযোগ" })}</div>
                      <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{shipment.customer_phone || shipment.customer_email || "—"}</div>
                    </div>
                    <div className="rounded-xl bg-[var(--rsi-bg-subtle)] p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-400">{pickLanguage(language, { en: "Status message", "zh-CN": "状态说明", bn: "স্ট্যাটাস বার্তা" })}</div>
                      <div className="mt-2 font-semibold text-[var(--rsi-navy)]">{shipment.status_detail || shipment.notes || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rsi-card p-6">
                    <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                      {pickLanguage(language, { en: "Customer app tools", "zh-CN": "客户应用工具", bn: "গ্রাহক অ্যাপ টুলস" })}
                    </h3>
                    <div className="mt-4 space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <Bell className="h-4 w-4 text-[var(--rsi-purple)] mt-0.5" />
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">{pickLanguage(language, { en: "Update channels", "zh-CN": "更新渠道", bn: "আপডেট চ্যানেল" })}</div>
                          <div className="text-gray-600">
                            {notificationChannels.length > 0
                              ? notificationChannels.map((item) => item.label).join(", ")
                              : pickLanguage(language, {
                                  en: "No live channels are enabled for this shipment yet.",
                                  "zh-CN": "该货运暂未启用实时提醒渠道。",
                                  bn: "এই চালানের জন্য এখনও কোনো লাইভ আপডেট চ্যানেল চালু করা হয়নি।",
                                })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <TimerReset className="h-4 w-4 text-[var(--rsi-purple)] mt-0.5" />
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">{pickLanguage(language, { en: "ETA window", "zh-CN": "预计到达窗口", bn: "ETA উইন্ডো" })}</div>
                          <div className="text-gray-600">{etaWindow(shipment, locale)}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <ClipboardList className="h-4 w-4 text-[var(--rsi-purple)] mt-0.5" />
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">{pickLanguage(language, { en: "Reference pack", "zh-CN": "参考资料包", bn: "রেফারেন্স প্যাক" })}</div>
                          <div className="text-gray-600">
                            Ref {shipment.reference_no || "—"} · {shipment.container_no || "No container"}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          await copyText(shipment.tracking_id, pickLanguage(language, {
                            en: "Tracking ID copied",
                            "zh-CN": "追踪编号已复制",
                            bn: "ট্র্যাকিং আইডি কপি করা হয়েছে",
                          }));
                        }}
                        className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold"
                      >
                        <Copy className="h-4 w-4" /> {pickLanguage(language, { en: "Copy tracking ID", "zh-CN": "复制追踪编号", bn: "ট্র্যাকিং আইডি কপি করুন" })}
                      </button>
                      <button
                        type="button"
                        onClick={shareTracking}
                        className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold"
                      >
                        <Share2 className="h-4 w-4" /> {pickLanguage(language, { en: "Share tracking link", "zh-CN": "分享追踪链接", bn: "ট্র্যাকিং লিংক শেয়ার করুন" })}
                      </button>
                      {!isStandalone && canInstall ? (
                        <button
                          type="button"
                          onClick={install}
                          className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold"
                        >
                          <Download className="h-4 w-4" /> {pickLanguage(language, { en: "Install this app", "zh-CN": "安装此应用", bn: "এই অ্যাপ ইনস্টল করুন" })}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="rsi-card p-6">
                    <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                      {pickLanguage(language, { en: "Need shipment support?", "zh-CN": "需要货运支持？", bn: "চালান সহায়তা দরকার?" })}
                    </h3>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                      {pickLanguage(language, {
                        en: "If you need a document confirmation, delay clarification, or consignee-side support, contact the trade desk with your tracking ID and reference number.",
                        "zh-CN": "如果您需要文件确认、延误说明或收货方支持，请携带追踪编号和参考号联系贸易团队。",
                        bn: "যদি ডকুমেন্ট নিশ্চিতকরণ, বিলম্ব ব্যাখ্যা বা গ্রাহকপক্ষের সহায়তা প্রয়োজন হয়, তবে আপনার ট্র্যাকিং আইডি ও রেফারেন্স নম্বরসহ ট্রেড ডেস্কে যোগাযোগ করুন।",
                      })}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <Link to="/contact" className="btn-primary !py-2.5 text-sm">
                        {pickLanguage(language, { en: "Contact Trade Desk", "zh-CN": "联系贸易团队", bn: "ট্রেড ডেস্কে যোগাযোগ করুন" })} <ArrowRight className="h-4 w-4" />
                      </Link>
                      <Link to="/trust-center" className="btn-ghost !py-2.5 text-sm">
                        {pickLanguage(language, { en: "View company documents", "zh-CN": "查看公司文件", bn: "কোম্পানির নথি দেখুন" })}
                      </Link>
                    </div>
                  </div>

                  {shipment.current_status === "delivered" && (
                    <div className="rsi-card p-6">
                      <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                        {pickLanguage(language, { en: "Proof of delivery", "zh-CN": "送达凭证", bn: "ডেলিভারির প্রমাণ" })}
                      </h3>
                      <div className="mt-4 space-y-3 text-sm text-gray-700">
                        <div className="flex items-start gap-3">
                          <Shield className="h-4 w-4 text-[var(--rsi-purple)] mt-0.5" />
                          <div>
                            <div className="font-semibold text-[var(--rsi-navy)]">{pickLanguage(language, { en: "Delivered at", "zh-CN": "送达时间", bn: "ডেলিভারির সময়" })}</div>
                            <div>{formatDate(shipment.delivered_at, locale)}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Phone className="h-4 w-4 text-[var(--rsi-purple)] mt-0.5" />
                          <div>
                            <div className="font-semibold text-[var(--rsi-navy)]">{pickLanguage(language, { en: "Received by", "zh-CN": "签收人", bn: "গ্রহণ করেছেন" })}</div>
                            <div>{shipment.receiver_name || shipment.customer_name}</div>
                          </div>
                        </div>
                        {shipment.delivery_proof_url && (
                          <img
                            src={shipment.delivery_proof_url}
                            alt="Delivery proof"
                            className="w-full h-40 object-cover rounded-xl border border-gray-100"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stepper */}
              <div className="mt-8 rsi-card p-6 lg:p-8">
                <h3 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)] mb-6">
                  {pickLanguage(language, { en: "Shipment progress", "zh-CN": "货运进度", bn: "চালানের অগ্রগতি" })}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {statusSteps.map((step, i) => {
                    const Icon = STATUS_ICONS[step.key] || Package;
                    const isDone = i <= currentStepIdx && shipment.current_status !== "delayed";
                    const isCurrent = i === currentStepIdx;
                    return (
                      <div
                        key={step.key}
                        className="flex flex-col items-center text-center"
                        data-testid={`step-${step.key}`}
                      >
                        <div
                          className={`h-12 w-12 rounded-full grid place-items-center transition-all duration-500 ${
                            isDone
                              ? "bg-[var(--rsi-purple)] text-white shadow-lg shadow-purple-900/30"
                              : "bg-gray-100 text-gray-400"
                          } ${isCurrent ? "ring-4 ring-[var(--rsi-purple)]/20" : ""}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className={`mt-2 text-xs font-semibold ${isDone ? "text-[var(--rsi-navy)]" : "text-gray-400"}`}>
                          {step.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Events */}
              <div className="mt-8 rsi-card p-6 lg:p-8">
                <h3 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)] mb-6">
                  {pickLanguage(language, { en: "Event log", "zh-CN": "事件记录", bn: "ইভেন্ট লগ" })}
                </h3>
                <div className="relative pl-8">
                  <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200" />
                  {[...shipment.events].reverse().map((ev, i) => {
                    const Icon = STATUS_ICONS[ev.status] || MapPin;
                    return (
                      <div key={i} className="relative pb-6 last:pb-0" data-testid={`event-${i}`}>
                        <div className="absolute -left-[22px] top-0 h-6 w-6 rounded-full grid place-items-center bg-[var(--rsi-purple)] text-white">
                          <Icon className="h-3 w-3" />
                        </div>
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <div className="font-semibold text-[var(--rsi-navy)]">
                            {statusLabels[ev.status] || ev.status} — {ev.location}
                          </div>
                          <div className="text-xs font-mono-plex text-gray-500">
                            {formatDate(ev.timestamp, locale)}
                          </div>
                        </div>
                        {ev.note && (
                          <div className="text-sm text-gray-600 mt-1">{ev.note}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {!shipment && !error && (
            <div className="rsi-card p-10 text-center" data-testid="tracking-placeholder">
              <Truck className="h-10 w-10 mx-auto text-gray-300" />
              <p className="mt-4 text-gray-500">
                {pickLanguage(language, {
                  en: "Enter a tracking ID above to view your shipment timeline and save it to this tracking app.",
                  "zh-CN": "在上方输入追踪编号，即可查看货运时间线并将其保存到此追踪应用中。",
                  bn: "উপরে একটি ট্র্যাকিং আইডি লিখে আপনার চালানের টাইমলাইন দেখুন এবং এটিকে এই ট্র্যাকিং অ্যাপে সংরক্ষণ করুন।",
                })}
              </p>
              {savedShipments.length > 0 && (
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  {savedShipments.map((item) => (
                    <button
                      key={item.tracking_id}
                      type="button"
                      onClick={() => loadSaved(item.tracking_id)}
                      className="rounded-full border border-gray-200 px-4 py-2 text-sm text-[var(--rsi-navy)] hover:border-[var(--rsi-purple)] hover:text-[var(--rsi-purple)]"
                    >
                      {item.tracking_id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
